import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { Download, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthAgoStr = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };

// Fix N (2026-09-04): panel de francos adeudados — francos absorbidos por coberturas
// (day_off_worked=True en schedule_coverage_changes) que la empresa todavía le debe
// a la persona, con posibilidad de registrar la devolución (compensación).
export function DayOffDebt({ clients = [] }) {
  const [filters, setFilters] = useState({ client_id: "", date_from: monthAgoStr(), date_to: todayStr() });
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [settleFor, setSettleFor] = useState(null);
  const [settleDate, setSettleDate] = useState(todayStr());
  const [settleNotes, setSettleNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const refresh = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.client_id) params.set("client_id", filters.client_id);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      const { data } = await api.get(`/day-off-debt/summary?${params.toString()}`);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters.client_id, filters.date_from, filters.date_to]);

  const toggleExpand = (uid) => setExpanded((e) => (e === uid ? null : uid));

  const openSettle = (uid) => { setSettleFor(uid); setSettleDate(todayStr()); setSettleNotes(""); };

  const submitSettle = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/day-off-debt/settle", { user_id: settleFor, date: settleDate, notes: settleNotes || null });
      toast.success("Franco compensado registrado");
      setSettleFor(null);
      refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const undoSettle = async (sid) => {
    if (!window.confirm("¿Deshacer esta compensación? El franco vuelve a quedar pendiente.")) return;
    try { await api.delete(`/day-off-debt/settle/${sid}`); toast.success("Compensación eliminada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportFile = async (kind) => {
    try {
      const params = new URLSearchParams();
      if (filters.client_id) params.set("client_id", filters.client_id);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      const res = await api.get(`/day-off-debt/export.${kind}?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `francos_adeudados_${filters.date_from}_${filters.date_to}.${kind}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="day-off-debt-panel">
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 14 }}>
        Cuando alguien de franco cubre un turno, ese franco queda adeudado. Acá se acumula cuántos trabajó cada persona, cuántos ya se le devolvieron y el pendiente.
      </p>
      <div className="card" style={{ padding: 14, marginBottom: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }} data-testid="dod-filters">
        <div className="field"><label>Objetivo</label>
          <select value={filters.client_id} onChange={(e) => setF("client_id", e.target.value)} data-testid="dod-filter-client">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Desde</label><input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} data-testid="dod-filter-from"/></div>
        <div className="field"><label>Hasta</label><input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} data-testid="dod-filter-to"/></div>
        <button type="button" className="btn-ghost" onClick={() => exportFile("csv")} style={{ alignSelf: "end", padding: "9px 14px" }} data-testid="dod-export-csv"><Download size={13} style={{ marginRight: 6 }}/>Exportar CSV</button>
        <button type="button" className="btn-ghost" onClick={() => exportFile("pdf")} style={{ alignSelf: "end", padding: "9px 14px" }} data-testid="dod-export-pdf"><Download size={13} style={{ marginRight: 6 }}/>Exportar PDF</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="dod-list">
        {rows.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin francos trabajados en el período.</div>}
        {rows.map((r) => (
          <div key={r.user_id} style={{ borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`dod-row-${r.user_id}`}>
            <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <button type="button" onClick={() => toggleExpand(r.user_id)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--invict-text-muted)" }} data-testid={`dod-expand-${r.user_id}`}>
                {expanded === r.user_id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{r.user_name}{r.user_deleted && <DeletedUserBadge id={r.user_id} />}</div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
                  {r.worked_count} franco{r.worked_count !== 1 ? "s" : ""} trabajado{r.worked_count !== 1 ? "s" : ""} · {r.settled_count} compensado{r.settled_count !== 1 ? "s" : ""}
                </div>
              </div>
              <span className="badge" style={{ background: r.pending_count > 0 ? "#fde8e8" : "#e8f5e9", color: r.pending_count > 0 ? "#BF3437" : "#2e7d32", fontWeight: 800 }} data-testid={`dod-pending-${r.user_id}`}>
                {r.pending_count} pendiente{r.pending_count !== 1 ? "s" : ""}
              </span>
              {r.pending_count > 0 && (
                <button type="button" className="btn-ghost" onClick={() => openSettle(r.user_id)} style={{ padding: "6px 10px", fontSize: 11 }} data-testid={`dod-settle-btn-${r.user_id}`}>
                  <CheckCircle2 size={13} style={{ marginRight: 4 }}/>Registrar devolución
                </button>
              )}
            </div>
            {expanded === r.user_id && (
              <div style={{ padding: "0 16px 14px 46px" }} data-testid={`dod-detail-${r.user_id}`}>
                {r.days.map((d, i) => (
                  <div key={i} style={{ fontSize: 12, padding: "4px 0", display: "flex", justifyContent: "space-between", borderBottom: i < r.days.length - 1 ? "1px dashed var(--invict-bone-3)" : "none" }} data-testid={`dod-day-${r.user_id}-${i}`}>
                    <span>{d.date} · {d.client_name}{d.day_off_worked_from_client_name ? ` (tenía franco en ${d.day_off_worked_from_client_name})` : ""}</span>
                    <span style={{ fontWeight: 700, color: d.settled ? "#2e7d32" : "#BF3437" }}>{d.settled ? `Compensado el ${d.settled_on}` : "Pendiente"}</span>
                  </div>
                ))}
                {r.settlements.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "var(--invict-text-muted)", marginBottom: 4 }}>Compensaciones registradas</div>
                    {r.settlements.map((s) => (
                      <div key={s.id} style={{ fontSize: 12, padding: "3px 0", display: "flex", justifyContent: "space-between" }} data-testid={`dod-settlement-${s.id}`}>
                        <span>{s.date}{s.notes ? ` · ${s.notes}` : ""}</span>
                        <button type="button" onClick={() => undoSettle(s.id)} style={{ border: "none", background: "transparent", color: "var(--invict-text-muted)", cursor: "pointer", fontSize: 11, textDecoration: "underline" }} data-testid={`dod-settlement-undo-${s.id}`}>Deshacer</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {settleFor && (
        <div className="modal-overlay" onClick={() => setSettleFor(null)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitSettle} data-testid="dod-settle-modal">
            <h3>Registrar devolución de franco</h3>
            <div className="field"><label>Fecha del franco compensatorio *</label>
              <input required type="date" value={settleDate} onChange={(e) => setSettleDate(e.target.value)} data-testid="dod-settle-date"/>
            </div>
            <div className="field"><label>Notas</label>
              <input value={settleNotes} onChange={(e) => setSettleNotes(e.target.value)} data-testid="dod-settle-notes"/>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => setSettleFor(null)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="dod-settle-submit">{busy ? "Guardando..." : "Confirmar"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
